import FormLabel from "@material-ui/core/FormLabel";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import React from "react";
import { ExamSessionViewModel, GetLocalizedName } from "../../model/ExamSessionViewModel";
import CustomDraggableDialog from "../common/customDialog/CustomDraggableDialog";
import DatePicker from "../common/datePicker/DatePicker";
import LocaleFormatDate from "../common/dates/LocaleFormatDate";
import LocaleFormatDateRange from "../common/dates/LocaleFormatDateRange";
import FormGridCell from "../common/FormGridCell";
import TranslatedMessage from "../translations/TranslatedMessage";
import SecretText from "../common/SecretText";
import CloseIcon from "@material-ui/icons/Close";
import DoneIcon from "@material-ui/icons/Done";
import LifeExamSessionLinks from "./LifeExamSessionLinks";
import SelfEditableTextField from "../common/textField/SelfEditableTextField";

import { useSetCandidatePinMutation, useSetExpertPinMutation } from "../../api/rtk/ExamSessionApi";
import LoadingIndicator from "../common/LoadingIndicator";

type ExamSessionFieldsReadonlyProps = {
  examSession: ExamSessionViewModel;
  isSurveyDialogOpen: boolean;
  isSurveyDeadlineEmpty: boolean;
  onAcceptSurvey: () => void;
  onCancelSurvey: () => void;
  onPropertyValueChanges?: (part: Partial<ExamSessionViewModel>) => void;
};

const ExamSessionFieldsReadonly: React.FunctionComponent<ExamSessionFieldsReadonlyProps> = ({
  examSession,
  isSurveyDialogOpen,
  isSurveyDeadlineEmpty: isSurveyDealineEmpty,
  onAcceptSurvey,
  onCancelSurvey,
  onPropertyValueChanges,
}) => {
  const [setCandidatePin, { isLoading: isSettingCandidatePin }] = useSetCandidatePinMutation();
  const [setExpertPin, { isLoading: isSettingExpertPin }] = useSetExpertPinMutation();

  const handleChange = (key: keyof ExamSessionViewModel, value: string | string[] | Date | boolean | undefined) => {
    let part: Partial<ExamSessionViewModel> = {
      [key]: value,
    };

    if (onPropertyValueChanges) {
      onPropertyValueChanges(part);
    }
  };
  const handleDateChange = (key: keyof ExamSessionViewModel) => (date: Date | null) => {
    if (date) {
      handleChange(key, date);
    }
  };

  const handleCancel = () => {
    onCancelSurvey();
    handleChange("surveyDeadline", undefined);
  };

  const handleCandidatePinChanged = (value: string) => {
    handleChange("candidatePin", value);
    setCandidatePin({ examSessionId: examSession.id, candidatePin: value }).unwrap();
  };

  const handleExpertPinChanged = (value: string) => {
    handleChange("expertPin", value);
    setExpertPin({ examSessionId: examSession.id, expertPin: value }).unwrap();
  };

  return (
    <Grid container={true} spacing={1}>
      <LoadingIndicator show={isSettingCandidatePin || isSettingExpertPin} />
      <FormGridCell size="half">
        <FormLabel component="legend">
          <Typography variant="body1">
            <TranslatedMessage id="examSession.field.sessionKey" />
          </Typography>
        </FormLabel>
      </FormGridCell>
      <FormGridCell size="half">
        <Typography variant="body1">{GetLocalizedName(examSession)}</Typography>
      </FormGridCell>

      <FormGridCell size="fill" />
      <FormGridCell size="half">
        <FormLabel component="legend">
          <Typography variant="body1">
            <TranslatedMessage id="examSession.field.date" />
          </Typography>
        </FormLabel>
      </FormGridCell>
      <FormGridCell size="half">
        <LocaleFormatDateRange dateFrom={examSession.from} dateTo={examSession.to} separator="-" />
      </FormGridCell>
      <FormGridCell size="fill" />
      <FormGridCell size="half">
        <FormLabel component="legend">
          <Typography variant="body1">
            <TranslatedMessage id="examSession.field.signupStartDate" />
          </Typography>
        </FormLabel>
      </FormGridCell>
      <FormGridCell size="half">
        <LocaleFormatDateRange
          dateFrom={examSession.signupStartDate}
          dateTo={examSession.signupEndDate}
          separator="-"
          showTime={true}
        />
      </FormGridCell>
      <FormGridCell size="fill" />
      <FormGridCell size="half">
        <FormLabel component="legend">
          <Typography variant="body1">
            <TranslatedMessage id="examSession.field.examSession" />
          </Typography>
        </FormLabel>
      </FormGridCell>
      <FormGridCell size="half">
        <LocaleFormatDateRange dateFrom={examSession.examsStartDate} dateTo={examSession.examsEndDate} separator="-" />
      </FormGridCell>
      <FormGridCell size="fill" />
      <FormGridCell size="half">
        <FormLabel component="legend">
          <Typography variant="body1">
            <TranslatedMessage id="examSession.field.freeCancellationEndDate" />
          </Typography>
        </FormLabel>
      </FormGridCell>
      <FormGridCell size="half">
        <LocaleFormatDate date={examSession.freeCancellationEndDate} variant="body1" />
      </FormGridCell>
      <FormGridCell size="fill" />
      <FormGridCell size="half">
        <FormLabel component="legend">
          <Typography variant="body1">
            <TranslatedMessage id="examSession.field.correctionPeriod" />
          </Typography>
        </FormLabel>
      </FormGridCell>
      <FormGridCell size="half">
        <LocaleFormatDateRange
          dateFrom={examSession.correctionStartDate}
          dateTo={examSession.correctionEndDate}
          separator="-"
        />
      </FormGridCell>
      <FormGridCell size="fill" />
      <FormGridCell size="half">
        <FormLabel component="legend">
          <Typography variant="body1">
            <TranslatedMessage id="examSession.field.gradingConferenceDate" />
          </Typography>
        </FormLabel>
      </FormGridCell>
      <FormGridCell size="half">
        <LocaleFormatDate date={examSession.gradingConferenceDate} variant="body1" />
      </FormGridCell>
      <FormGridCell size="fill" />
      <FormGridCell size="half">
        <FormLabel component="legend">
          <Typography variant="body1">
            <TranslatedMessage id="examSession.field.examResultDate" />
          </Typography>
        </FormLabel>
      </FormGridCell>
      <FormGridCell size="half">
        <LocaleFormatDate date={examSession.examResultDate} variant="body1" />
      </FormGridCell>
      <FormGridCell size="fill" />
      <FormGridCell size="half">
        <FormLabel component="legend">
          <Typography variant="body1">
            <TranslatedMessage id="examSession.field.inspectionSignupEndDate" />
          </Typography>
        </FormLabel>
      </FormGridCell>
      <FormGridCell size="half">
        <LocaleFormatDate date={examSession.objectionDate} variant="body1" />
      </FormGridCell>
      <FormGridCell size="fill" />

      <FormGridCell size="half">
        <FormLabel component="legend">
          <Typography variant="body1">
            <TranslatedMessage id="examSession.field.inspectionPeriod" />
          </Typography>
        </FormLabel>
      </FormGridCell>
      <FormGridCell size="half">
        <LocaleFormatDateRange
          dateFrom={examSession.inspectionStartDate}
          dateTo={examSession.inspectionEndDate}
          separator="-"
        />
      </FormGridCell>
      <FormGridCell size="fill" />

      <FormGridCell size="half">
        <FormLabel component="legend">
          <Typography variant="body1">
            <TranslatedMessage id="examSession.field.candidatePin" />
          </Typography>
        </FormLabel>
      </FormGridCell>
      <FormGridCell size="half">
        <SelfEditableTextField
          readOnlyComponent={
            <SecretText text={examSession.candidatePin} label="examSession.label.revealCandidatePin" />
          }
          value={examSession.candidatePin}
          onChange={handleCandidatePinChanged}
        />
      </FormGridCell>
      <FormGridCell size="fill" />

      <FormGridCell size="half">
        <FormLabel component="legend">
          <Typography variant="body1">
            <TranslatedMessage id="examSession.field.expertPin" />
          </Typography>
        </FormLabel>
      </FormGridCell>
      <FormGridCell size="half">
        <SelfEditableTextField
          readOnlyComponent={<SecretText text={examSession.expertPin} label="examSession.label.revealExpertPin" />}
          value={examSession.expertPin}
          onChange={handleExpertPinChanged}
        />
      </FormGridCell>
      <FormGridCell size="fill" />

      <FormGridCell size="half">
        <FormLabel component="legend">
          <Typography variant="body1">
            <TranslatedMessage id="examSession.field.isLife" />
          </Typography>
        </FormLabel>
      </FormGridCell>
      <FormGridCell size="half">
        {examSession.isLife ? <DoneIcon style={{ width: "24px" }} /> : <CloseIcon style={{ width: "24px" }} />}
      </FormGridCell>
      <FormGridCell size="fill" />
      {examSession.isLife && (
        <FormGridCell size="wide">
          <LifeExamSessionLinks examSessionId={examSession.id} />
        </FormGridCell>
      )}

      <CustomDraggableDialog
        acceptText={<TranslatedMessage id="common.accept" />}
        cancelText={<TranslatedMessage id="common.cancel" />}
        content={
          <DatePicker
            label={<TranslatedMessage id="examSession.field.surveyDeadline" />}
            value={examSession.surveyDeadline}
            onChange={handleDateChange("surveyDeadline")}
            disablePast={true}
            required={true}
            error={isSurveyDealineEmpty}
          />
        }
        isOpen={isSurveyDialogOpen}
        onAccept={() => onAcceptSurvey()}
        onCancel={handleCancel}
        title={<TranslatedMessage id="examSession.field.surveyDeadline" />}
      />
    </Grid>
  );
};

ExamSessionFieldsReadonly.defaultProps = {
  onPropertyValueChanges: () => {},
  onAcceptSurvey: () => {},
  onCancelSurvey: () => {},
} as Partial<ExamSessionFieldsReadonlyProps>;

export default ExamSessionFieldsReadonly;
